import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Link, Stack, Typography } from '@mui/material';
import CopyableText from 'components/atoms/CopyableText';
import { JobArchiveControl, useJobArchiveAction } from 'components/Pages/Jobs/JobArchiveControl';
import { getJobStatusDisplayLabel, JobStatusChip } from 'components/Pages/Jobs/JobStatusChip';
import { DuplicateIngest, UploadDashboardRow } from 'interfaces/fileList';
import { JobStatus } from 'interfaces/job';
import { filter, join, map, size } from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDateTimeForUser } from 'utils/dateTime';

interface UploadIngestTimelineProps {
  ingests: DuplicateIngest[];
  rowLabel: string;
  getJobUrl: (jobId: string, archived: boolean) => string | null;
  /** Duplicate-ingest rows carry job ownership. Single-attempt rows do not, so they omit the action. */
  showArchiveAction?: boolean;
}

const getIngestDate = ({ ingestJob, slideCreatedAt }: DuplicateIngest) => {
  const date = ingestJob?.startedAt || slideCreatedAt;
  return date ? formatDateTimeForUser(date) : 'Start date unavailable';
};

const getCaseLabel = (ingest: DuplicateIngest) =>
  ingest.cases.length ? join(map(ingest.cases, 'displayName'), ', ') : 'Not assigned';

export const getUploadTimelineIngests = (
  row: Pick<UploadDashboardRow, 'duplicateIngests' | 'latestIngestJob' | 'slideId' | 'cases'>
): DuplicateIngest[] => {
  if (size(row.duplicateIngests)) {
    return row.duplicateIngests;
  }

  if (!row.latestIngestJob) {
    return [];
  }

  return [
    {
      slideId: row.slideId || null,
      slideCreatedAt: null,
      ingestJob: { ...row.latestIngestJob, archivedAt: null, userId: null },
      isArchived: false,
      isCurrent: true,
      cases: row.cases || [],
      hasFurtherJobs: false,
      suggestedForArchive: false,
    },
  ];
};

export const getUploadTimelineSearchText = (ingests: DuplicateIngest[]) =>
  join(
    map(ingests, (ingest) =>
      join(
        [
          getIngestDate(ingest),
          ingest.ingestJob?.status,
          ingest.ingestJob?.statusMessage,
          ingest.slideId || 'No slide created',
          getCaseLabel(ingest),
          ingest.isCurrent ? 'Current' : null,
          ingest.isArchived ? 'Rejected' : null,
          ingest.suggestedForArchive ? 'Suggested to reject' : null,
        ].filter(Boolean),
        ' '
      )
    ),
    ' '
  );

const DetailLine = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 36, flexShrink: 0, lineHeight: 1.45 }}>
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
  </Stack>
);

export const getIngestArchiveCopy = (hasSlide: boolean) =>
  hasSlide
    ? {
        archiveLabel: 'Reject ingest',
        restoreLabel: 'Restore ingest',
        archiveText:
          'Rejecting this ingest makes that attempt not usable. The uploaded file, slide record, and existing case assignments stay, and the file is not hidden. If no other active ingest uses that slide, it is not available for case assignment until you restore this ingest.',
        restoreText:
          'Restoring this ingest makes that attempt usable again. Existing case assignments stay unchanged. If no other active ingest uses that slide, it is available for case assignment again unless the slide is hidden.',
      }
    : {
        archiveLabel: 'Reject ingest',
        restoreLabel: 'Restore ingest',
        archiveText:
          'Rejecting this ingest makes that attempt not usable. This attempt did not create a slide. The uploaded file stays and is not hidden.',
        restoreText:
          'Restoring this ingest makes that attempt usable again. It did not create a slide. The uploaded file stays.',
      };

const ArchiveAction = ({
  job,
  hasSlide,
}: {
  job: {
    id: string;
    name: string;
    status?: string;
    userId?: string;
    deletedAt?: string;
  };
  hasSlide: boolean;
}) => {
  const archiveAction = useJobArchiveAction(job);
  const copy = getIngestArchiveCopy(hasSlide);

  if (!archiveAction.isAvailable) {
    return null;
  }

  return (
    <JobArchiveControl
      labeled
      contained
      actionLabels={{ archive: copy.archiveLabel, restore: copy.restoreLabel }}
      confirmationText={{ archive: copy.archiveText, restore: copy.restoreText }}
      job={job}
    />
  );
};

const IngestTimelineRow = ({
  ingest,
  rowLabel,
  jobUrl,
  showCurrent,
  showDetails,
  showArchiveAction,
}: {
  ingest: DuplicateIngest;
  rowLabel: string;
  jobUrl: string | null;
  showCurrent: boolean;
  showDetails: boolean;
  showArchiveAction: boolean;
}) => {
  const { ingestJob, slideId, isArchived, suggestedForArchive } = ingest;
  const ingestDate = getIngestDate(ingest);
  const statusLabel = ingestJob ? getJobStatusDisplayLabel(ingestJob.status || undefined) : 'No ingest job';
  const statusChip = ingestJob ? (
    <JobStatusChip status={ingestJob.status || undefined} label={statusLabel} />
  ) : (
    <Typography variant="body2">{statusLabel}</Typography>
  );
  const status = (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
      {jobUrl && ingestJob ? (
        <Link
          component={RouterLink}
          to={jobUrl}
          underline="none"
          aria-label={`Open ingest job from ${ingestDate}`}
          sx={{ display: 'inline-flex' }}
        >
          {statusChip}
        </Link>
      ) : (
        statusChip
      )}
      {showCurrent ? <Chip size="small" variant="outlined" label="Current" /> : null}
      {isArchived ? <Chip size="small" variant="outlined" label="Rejected" /> : null}
      {suggestedForArchive ? <Chip size="small" color="warning" label="Suggested to reject" /> : null}
    </Stack>
  );
  const message = ingestJob?.statusMessage && ingestJob.status !== JobStatus.Completed ? ingestJob.statusMessage : null;
  const archiveJob = ingestJob
    ? {
        id: ingestJob.id,
        name: `ingest of ${rowLabel} from ${ingestDate}`,
        status: ingestJob.status || undefined,
        userId: ingestJob.userId || undefined,
        deletedAt: ingestJob.archivedAt || undefined,
      }
    : null;

  return (
    <Box
      component="li"
      aria-label={`Ingest of ${rowLabel} from ${ingestDate}`}
      sx={{
        listStyle: 'none',
        px: showDetails ? 1 : 0,
        py: showDetails ? 1 : 0.25,
        borderRadius: 1,
        bgcolor: showDetails ? 'action.hover' : 'transparent',
        '& + &': { mt: 0.75 },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
              {ingestDate}
            </Typography>
            {status}
          </Stack>
          {message ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45, overflowWrap: 'anywhere' }}>
              {message}
            </Typography>
          ) : null}
          {showDetails ? (
            <Stack spacing={0.5}>
              <DetailLine label="Slide">
                {slideId ? (
                  <CopyableText
                    variant="body2"
                    textForCopy={slideId}
                    tooltipText="Copy slide ID"
                    copyWithButton="right"
                    copyButtonVisibility="always"
                    whiteSpace="normal"
                    copyButtonProps={{ size: 'small', 'aria-label': `Copy slide ID ${slideId}` }}
                    copyIconProps={{ fontSize: 'small' }}
                    parentGridProps={{ sx: { minWidth: 0, maxWidth: '100%' } }}
                    sx={{ lineHeight: 1.45, overflowWrap: 'anywhere', whiteSpace: 'normal' }}
                  >
                    {slideId}
                  </CopyableText>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No slide created
                  </Typography>
                )}
              </DetailLine>
              <DetailLine label="Case">
                <Typography
                  variant="body2"
                  color={ingest.cases.length ? 'text.primary' : 'text.secondary'}
                  sx={{ lineHeight: 1.45, overflowWrap: 'anywhere' }}
                >
                  {getCaseLabel(ingest)}
                </Typography>
              </DetailLine>
            </Stack>
          ) : null}
        </Stack>
        {showArchiveAction && archiveJob ? <ArchiveAction hasSlide={Boolean(slideId)} job={archiveJob} /> : null}
      </Stack>
    </Box>
  );
};

/**
 * One timeline row per ingest. Status owns the current outcome. A single attempt stays one line;
 * extra attempts keep slide and case because those can differ.
 */
const UploadIngestTimeline = ({
  ingests,
  rowLabel,
  getJobUrl,
  showArchiveAction = false,
}: UploadIngestTimelineProps) => {
  const [showArchived, setShowArchived] = React.useState(false);

  if (!size(ingests)) {
    return null;
  }
  const archivedIngests = filter(ingests, 'isArchived');
  const openIngests = filter(ingests, (ingest) => !ingest.isArchived);
  const archivedCount = size(archivedIngests);
  const showDetails = ingests.length > 1;
  const archivedLabel = `${archivedCount} rejected ${archivedCount === 1 ? 'ingest' : 'ingests'}`;

  const renderIngest = (ingest: DuplicateIngest) => (
    <IngestTimelineRow
      key={`${ingest.ingestJob?.id ?? 'no-job'}:${ingest.slideId ?? 'no-slide'}`}
      ingest={ingest}
      rowLabel={rowLabel}
      jobUrl={ingest.ingestJob ? getJobUrl(ingest.ingestJob.id, ingest.isArchived) : null}
      showCurrent={ingest.isCurrent && showDetails}
      showDetails={showDetails}
      showArchiveAction={showArchiveAction}
    />
  );

  return (
    <Stack spacing={0.75} sx={{ minWidth: 0, width: '100%' }}>
      {size(openIngests) ? (
        <Box component="ol" aria-label={`Ingest timeline of ${rowLabel}`} sx={{ m: 0, p: 0 }}>
          {openIngests.map(renderIngest)}
        </Box>
      ) : null}
      {archivedCount ? (
        <Stack spacing={0.75}>
          <Button
            size="small"
            color="inherit"
            aria-expanded={showArchived}
            onClick={() => setShowArchived((current) => !current)}
            endIcon={showArchived ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ alignSelf: 'flex-start', px: 0.5, textTransform: 'none' }}
          >
            {showArchived ? `Hide ${archivedLabel}` : `Show ${archivedLabel}`}
          </Button>
          {showArchived ? (
            <Box component="ol" aria-label={`Rejected ingests of ${rowLabel}`} sx={{ m: 0, p: 0 }}>
              {archivedIngests.map(renderIngest)}
            </Box>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default UploadIngestTimeline;
